import { WORKFLOW_TOOLS } from '../../toolset/catalog';
import type { GeneratedToolAccessModes } from '../../toolset/generated/toolCatalog.generated';

// What an allowed_tools entry grants, in words an operator reads. An entry is 'tool' or
// 'tool:mode'; a plain mode tool grants only its least mode (ADR-0055). The modes come from the
// generated tool catalog, the same registry facts the backend checks a publish against.

const MODES: ReadonlyMap<string, GeneratedToolAccessModes> = new Map(
  WORKFLOW_TOOLS.flatMap((t) => (t.accessModes ? [[t.name, t.accessModes] as const] : [])),
);

export const modesOf = (tool: string): GeneratedToolAccessModes | undefined => MODES.get(tool);

// A mode in words, from its own spelling: underscores become spaces and a fused '...only' splits,
// so 'readonly' reads "read only" and a mode added later still reads sensibly.
const modeWords = (mode: string): string =>
  mode.replace(/_/g, ' ').replace(/(\w)only\b/g, '$1 only');

const ESCALATION_TOOLS: ReadonlySet<string> = new Set(
  WORKFLOW_TOOLS.filter((t) => t.escalation).map((t) => t.name),
);

// 'use_aws_customer' reads "AWS customer", 'validate_role_access' "Validate role access".
const toolWords = (tool: string): string => {
  const words = tool
    .replace(/^use_/, '')
    .split('_')
    .map((w) => (w === 'aws' ? 'AWS' : w))
    .join(' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

export const parseGrant = (entry: string): { tool: string; mode: string | null } => {
  const [tool, ...rest] = entry.split(':');
  return { tool, mode: rest.length > 0 ? rest.join(':') : null };
};

/** 'use_aws_customer:write' reads "AWS customer: write"; a plain mode tool says its least mode. */
export const grantLabel = (entry: string): string => {
  const { tool, mode } = parseGrant(entry);
  const spec = modesOf(tool);
  if (!spec) return entry;
  const shown = mode ?? spec.least;
  const words = modeWords(shown);
  return mode === null ? `${toolWords(tool)} (${words})` : `${toolWords(tool)}: ${words}`;
};

/** Why an entry cannot be published, or null when its tool and mode are ones the backend accepts. */
export const grantProblem = (entry: string): string | null => {
  const { tool, mode } = parseGrant(entry);
  if (ESCALATION_TOOLS.has(tool)) {
    return `'${tool}' changes an operator's access; a workflow cannot, the operator escalates themselves.`;
  }
  if (mode === null) return null;
  const spec = modesOf(tool);
  if (!spec) return `'${entry}': '${tool}' has no access modes; name it alone.`;
  if (!spec.modes.includes(mode)) {
    return `'${entry}': '${mode}' is not a mode of '${tool}'; its modes are ${spec.modes.join(', ')}.`;
  }
  return null;
};
